import { compileDisplaySetSplitRules } from './compileSplitRules.js';
const VOLUME_MODALITIES = ['CT', 'MR', 'PT', 'NM'];
const CINE_MODALITIES = ['US', 'XA', 'RF', 'ES', 'XC'];
const VOLUME_MULTIFRAME_SOP_CLASSES = [
    '1.2.840.10008.5.1.4.1.1.6.2',
    '1.2.840.10008.5.1.4.1.1.13.1.1',
    '1.2.840.10008.5.1.4.1.1.13.1.2',
];
const quotedList = (values) => `[${values.map((v) => `'${v}'`).join(', ')}]`;
const SINGLE_IMAGE_MODALITIES = ['CR', 'DX', 'MG'];
const RENDERABLE_IMAGE = { isImage: true, Rows: { notNull: true } };
const REVISION_CHAIN = {
    groupBy: ['SeriesInstanceUID', 'revisionChainUid'],
    sortBy: ['isActiveRevision', 'InstanceNumber'],
    attributes: { activeSopInstanceUid: 'revisionChainUid' },
};
const objectRule = (priority, kind, handler, description) => ({
    priority,
    description,
    handler,
    matches: { [kind]: true },
    groupBy: ['SeriesInstanceUID'],
});
export const displaySetRules = {
    video: {
        priority: 1,
        description: 'A video-only SOP class, or a video transfer syntax, or (with no known ' +
            'transfer syntax) a long multi-frame secondary capture. One display set ' +
            'per instance, shown on a video viewport.',
        handler: 'video',
        matches: { isVideo: true },
        groupBy: ['SOPInstanceUID'],
    },
    ecg: {
        priority: 2,
        description: 'ECG / waveform SOP classes. One display set per instance, shown on a ' +
            'waveform viewport rather than an image viewport.',
        handler: 'ecg',
        matches: { isEcg: true },
        groupBy: ['SOPInstanceUID'],
    },
    wholeslide: {
        priority: 3,
        description: 'VL Whole Slide Microscopy (or modality SM). All pyramid levels of the ' +
            'series form a single whole-slide display set.',
        handler: 'wholeSlide',
        matches: { isWsi: true },
        groupBy: ['SeriesInstanceUID'],
    },
    singleImageModality: {
        priority: 4,
        description: 'CR / DX / MG, which acquire one image per instance. One display set ' +
            'per instance, so each view of a mammography series is its own display ' +
            'set.',
        matches: { Modality: SINGLE_IMAGE_MODALITIES, isImage: true },
        groupBy: ['SOPInstanceUID'],
    },
    multiFrame: {
        priority: 5,
        description: 'Multi-frame image instances, one display set per instance. A cine clip ' +
            '(a slice location, or a US, XA, RF, ES or XC multi-frame that is not a ' +
            'volume) is flagged isClip. Another multi-frame instance, such as an ' +
            'enhanced CT, is not a clip.',
        context: {
            isCineClip: `SliceLocation != null || (Modality in ${quotedList(CINE_MODALITIES)}` +
                ` && !(SOPClassUID in ${quotedList(VOLUME_MULTIFRAME_SOP_CLASSES)}))`,
        },
        matches: { isImage: true, isMultiFrame: true },
        groupBy: ['SOPInstanceUID'],
        attributes: {
            isClip: 'isCineClip',
            numImageFrames: { expression: 'Number(NumberOfFrames)' },
        },
    },
    mixedDimensionalityBValue: {
        priority: 6,
        description: 'Diffusion MR that mixes 4D b-value frames with trailing frames that have ' +
            'none. The undefined-b-value frames are not part of the 4D set, so they ' +
            'split off - otherwise 4D rendering is applied to the 3D portion.',
        context: {
            isMixedBValue: 'series.instances.count(DiffusionBValue) > 0 && ' +
                'series.instances.count(DiffusionBValue) < series.instances.count()',
            hasNoBValue: 'DiffusionBValue == null',
        },
        matches: { Modality: 'MR', isMixedBValue: true, ...RENDERABLE_IMAGE },
        groupBy: ['SeriesInstanceUID', 'hasNoBValue'],
    },
    volume3d: {
        priority: 7,
        description: 'Multi-slice CT / MR / PT / NM, which reconstruct into a volume.',
        matches: {
            Modality: VOLUME_MODALITIES,
            seriesInstanceCount: { greaterThan: 1 },
            ...RENDERABLE_IMAGE,
        },
        groupBy: ['SeriesInstanceUID'],
    },
    defaultImageRule: {
        priority: 8,
        description: 'Fallback for any remaining image SOP class, also with no Rows in the ' +
            'metadata. Grouped one display set per series.',
        matches: { isImage: true },
    },
    segmentation: objectRule(9, 'isSegmentation', 'segmentation', 'Segmentations (segmentationSopClassUids). One display set per series.'),
    rtStructureSet: objectRule(10, 'isRtStructureSet', 'rtStructureSet', 'RT structure sets (rtStructureSetSopClassUids). One display set per series.'),
    parametricMap: objectRule(11, 'isParametricMap', 'parametricMap', 'Parametric maps (parametricMapSopClassUids). One display set per series.'),
    structuredReport: {
        ...objectRule(12, 'isStructuredReport', 'structuredReport', 'Structured reports (structuredReportSopClassUids), also Comprehensive ' +
            '3D SR. One display set per series. Manufacturer and ' +
            'ManufacturerModelName name the writer of the SR.'),
        attributes: {
            Manufacturer: 'Manufacturer',
            ManufacturerModelName: 'ManufacturerModelName',
        },
    },
    encapsulatedDocument: {
        ...objectRule(13, 'isEncapsulatedDocument', 'encapsulatedDocument', 'Encapsulated documents (encapsulatedDocumentSopClassUids). One ' +
            'display set per revision chain (PredecessorDocumentsSequence) of ' +
            'the series; activeSopInstanceUid names the current revision.'),
        ...REVISION_CHAIN,
    },
    bulkAnnotation: objectRule(14, 'isBulkAnnotation', 'bulkAnnotation', 'Microscopy bulk annotations (bulkAnnotationSopClassUids). One display ' +
        'set per series.'),
    chart: {
        ...objectRule(15, 'isChart', 'chart', 'Charts (chartSopClassUids, empty by default: a host adds its chart ' +
            'SOP class). One display set per revision chain of the series; ' +
            'activeSopInstanceUid names the current revision.'),
        ...REVISION_CHAIN,
    },
    unsupported: {
        priority: Number.MAX_SAFE_INTEGER,
        description: 'Catch-all for objects that no other rule claims (presentation states, ' +
            'other non-image objects). Produces one display set for each series, ' +
            'marked isDisplayable: false, so the objects are surfaced rather than ' +
            'silently dropped. Runs last.',
        handler: 'unsupported',
        groupBy: ['SeriesInstanceUID'],
    },
};
export function createDisplaySetSplitRules(rules = displaySetRules, options = {}) {
    return compileDisplaySetSplitRules(rules, options);
}
